/**
 * Authentication API
 * ==================
 * API methods for authentication operations
 */

import apiClient, { tokenManager } from './client';

const authAPI = {
  /**
   * Login with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise} Login response with tokens and user data
   */
  login: async (email, password, orgSlug, captchaToken = null) => {
    const body = { email, password };
    if (orgSlug) body.org_slug = orgSlug;
    if (captchaToken) body.captcha_token = captchaToken;
    const response = await apiClient.post('/api/v1/auth/login', body);

    // Store tokens
    const { access_token, refresh_token } = response.data;
    tokenManager.setTokens(access_token, refresh_token);

    return response.data;
  },

  /**
   * Logout current user
   * @returns {Promise} Logout response
   */
  logout: async () => {
    try {
      await apiClient.post('/api/v1/auth/logout');
    } finally {
      // Always clear tokens, even if API call fails
      tokenManager.clearTokens();
    }
  },

  /**
   * Refresh access token
   * @returns {Promise} New access token
   */
  refreshToken: async () => {
    const refreshToken = tokenManager.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await apiClient.post('/api/v1/auth/refresh', {
      refresh_token: refreshToken,
    });

    const { access_token } = response.data;
    tokenManager.setTokens(access_token, refreshToken);

    return response.data;
  },

  /**
   * Get current user information
   * @returns {Promise} Current user data
   */
  getCurrentUser: async () => {
    const response = await apiClient.get('/api/v1/auth/me');
    return response.data.data; // Extract data from APIResponse wrapper
  },

  /**
   * Change password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @param {string} confirmPassword - Confirm new password
   * @returns {Promise} Password change response
   */
  changePassword: async (currentPassword, newPassword, confirmPassword) => {
    const response = await apiClient.post('/api/v1/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });

    // Clear tokens if must login again
    if (response.data.must_login_again) {
      tokenManager.clearTokens();
    }

    return response.data;
  },

  /**
   * Request password reset email
   * @param {string} email - User email
   * @returns {Promise} Password reset response
   */
  forgotPassword: async (email, captchaToken = null) => {
    const body = { email };
    if (captchaToken) body.captcha_token = captchaToken;
    const response = await apiClient.post('/api/v1/auth/forgot-password', body);
    return response.data;
  },

  /**
   * Reset password with token
   * @param {string} token - Reset token from email
   * @param {string} newPassword - New password
   * @param {string} confirmPassword - Confirm new password
   * @returns {Promise} Password reset confirmation response
   */
  resetPassword: async (token, newPassword, confirmPassword) => {
    const response = await apiClient.post('/api/v1/auth/reset-password', {
      token,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
    return response.data;
  },

  /**
   * Switch active organization
   */
  switchOrg: async (organizationId) => {
    const response = await apiClient.post('/api/v1/auth/switch-org', {
      organization_id: organizationId,
    });

    const { access_token } = response.data.data;
    if (access_token) {
      const refreshToken = tokenManager.getRefreshToken();
      tokenManager.setTokens(access_token, refreshToken);
    }

    return response.data.data;
  },

  /**
   * Check if user is authenticated
   * @returns {boolean} True if user has access token
   */
  isAuthenticated: () => {
    return !!tokenManager.getAccessToken();
  },
};

export default authAPI;

